<script setup lang="ts">
import { Bug, FilterX, MessageSquareText, TriangleAlert } from "@lucide/vue";
import { refDebounced } from "@vueuse/core";
import { computed, h } from "vue";
import type { FeedbackFilter } from "@/api/feedback";
import { useFeedbackList, useFeedbackSummary } from "@/api/feedback";
import type { FeedbackItem, FeedbackKind, FeedbackStatus } from "@/api/contracts";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import FilterChips from "@/components/common/FilterChips.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import RelativeTime from "@/components/common/RelativeTime.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import StatCard from "@/components/common/StatCard.vue";
import FeedbackSheet from "@/components/feedback/FeedbackSheet.vue";
import { KIND_TONE, KINDS, kindLabel, STATUS_TONE, STATUSES, statusLabel } from "@/components/feedback/feedback";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { t } from "@/copy/strings";
import { formatNumber } from "@/lib/format";
import type { Column } from "@/lib/table";

definePage({ meta: { title: "Feedback", requires: { capability: "feedback.admin" }, layout: "wide" } });

const q = useRouteQuery("q");
const kind = useRouteQuery("kind");
const status = useRouteQuery("status");
const tool = useRouteQuery("tool");
/** The open item (the digest links to /admin/feedback?id=…). */
const selected = useRouteQuery("id");
const qDebounced = refDebounced(q, 300);
const toolDebounced = refDebounced(tool, 400);

const filter = computed<FeedbackFilter>(() => ({
  q: qDebounced.value || undefined,
  kind: (kind.value || undefined) as FeedbackKind | undefined,
  status: (status.value || undefined) as FeedbackStatus | undefined,
  tool: toolDebounced.value || undefined,
}));
const list = useFeedbackList(filter);
const items = computed(() => list.data.value?.pages.flatMap((p) => p.items) ?? []);
const summary = useFeedbackSummary();

const fields = [q, kind, status, tool];
const anyFilter = computed(() => fields.some((r) => r.value));
const clearAll = () => {
  for (const r of fields) r.value = "";
};

const openId = computed(() => {
  const n = Number(selected.value);
  return Number.isInteger(n) && n > 0 ? n : null;
});
const sheetOpen = computed({
  get: () => openId.value !== null,
  set: (v: boolean) => {
    if (!v) selected.value = "";
  },
});

const kindOptions = KINDS.map((k) => ({ value: k, label: kindLabel(k) }));
const statusChips = STATUSES.map((s) => ({ value: s, label: statusLabel(s), count: summary.data.value?.counts.byStatus[s] }));

const columns = computed<Column<FeedbackItem>[]>(() => [
  { id: "when", header: t.feedback.when, accessorFn: (f) => f.createdAt, meta: { mobile: "subtitle" }, cell: ({ row }) => h(RelativeTime, { value: row.original.createdAt, class: "text-sm whitespace-nowrap" }) },
  {
    id: "kind",
    header: t.feedback.kind,
    accessorFn: (f) => f.kind,
    meta: { mobile: "badge" },
    cell: ({ row }) => h(Badge, { variant: KIND_TONE[row.original.kind] }, () => kindLabel(row.original.kind)),
  },
  {
    id: "message",
    header: t.feedback.message,
    accessorFn: (f) => f.message,
    meta: { mobile: "title" },
    cell: ({ row }) =>
      h("div", { class: "whitespace-normal sm:min-w-64" }, [
        h("p", { class: "line-clamp-2 text-sm [overflow-wrap:anywhere]" }, row.original.message),
        row.original.tool || row.original.subject
          ? h("p", { class: "mt-0.5 truncate font-mono text-xs font-normal text-muted-foreground" }, [row.original.tool, row.original.subject].filter(Boolean).join(" · "))
          : null,
      ]),
  },
  { id: "from", header: t.feedback.from, accessorFn: (f) => f.username ?? "", cell: ({ row }) => h("span", { class: "text-sm whitespace-nowrap" }, row.original.username ?? "—") },
  {
    id: "status",
    header: t.feedback.status,
    accessorFn: (f) => f.status,
    meta: { mobile: "meta" },
    cell: ({ row }) => h(Badge, { variant: STATUS_TONE[row.original.status] }, () => statusLabel(row.original.status)),
  },
]);

const show = (f: FeedbackItem) => {
  selected.value = String(f.id);
};
const clusters = computed(() => summary.data.value?.errorClusters ?? []);
</script>

<template>
  <div>
    <PageHeader :crumbs="[{ label: t.admin.title, to: '/admin' }]" :title="t.feedback.title" :description="t.feedback.description" />

    <section aria-label="Overview" class="mb-5 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
      <div class="grid grid-cols-3 gap-3 lg:grid-cols-1">
        <StatCard :label="t.feedback.openItems" :value="summary.data.value ? formatNumber(summary.data.value.counts.byStatus.open) : null" :icon="MessageSquareText" :loading="summary.isPending.value" />
        <StatCard :label="t.feedback.openBugs" :value="summary.data.value ? formatNumber(summary.data.value.counts.openBugs) : null" :icon="Bug" tone="danger" :loading="summary.isPending.value" />
        <StatCard :label="t.feedback.newThisWeek" :value="summary.data.value ? formatNumber(summary.data.value.counts.last7Days) : null" :loading="summary.isPending.value" />
      </div>
      <SectionCard :title="t.feedback.errorsTitle" :icon="TriangleAlert" :description="t.feedback.errorsDescription">
        <p v-if="summary.data.value && !clusters.length" class="px-4 pb-4 text-sm text-muted-foreground sm:px-5">{{ t.feedback.errorsEmpty }}</p>
        <ul v-else-if="clusters.length" class="divide-y px-1 pb-2">
          <li v-for="c in clusters" :key="`${c.tool}/${c.errorCode}`">
            <button
              type="button"
              class="flex w-full min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5 rounded-md px-3 py-2 text-left transition-colors hover:bg-state-hover active:bg-state-press"
              :title="t.feedback.filterByTool"
              @click="tool = c.tool"
            >
              <span class="font-mono text-sm font-medium">{{ c.tool }}</span>
              <Badge variant="outline" class="font-mono">{{ c.errorCode }}</Badge>
              <span class="text-sm text-muted-foreground tabular-nums">{{ formatNumber(c.calls) }} {{ t.feedback.calls }} · {{ formatNumber(c.people) }} {{ t.feedback.people }}</span>
              <span v-if="c.sample" class="min-w-0 basis-full truncate text-xs text-muted-foreground">{{ c.sample }}</span>
            </button>
          </li>
        </ul>
      </SectionCard>
    </section>

    <FilterBar v-model:search="q" placeholder="Message or subject" :search-label="t.feedback.text" label="Filter feedback" class="mb-5">
      <SimpleSelect v-model="kind" :options="kindOptions" allow-empty :empty-label="t.common.all" placeholder="All kinds" class="w-full sm:w-44" :aria-label="t.feedback.kind" />
      <Input v-model="tool" :placeholder="`${t.feedback.tool}, e.g. mysql_query`" class="w-full sm:w-56" :aria-label="t.feedback.tool" />
      <template #end>
        <Button v-if="anyFilter" variant="ghost" @click="clearAll"><FilterX /> {{ t.common.clear }}</Button>
      </template>
      <template #chips>
        <FilterChips v-model="status" :options="statusChips" :label="t.feedback.status" />
      </template>
    </FilterBar>

    <ListSkeleton v-if="list.isPending.value" :rows="8" />
    <ErrorState v-else-if="list.error.value" :error="list.error.value" @retry="list.refetch()" />
    <EmptyState v-else-if="items.length === 0" :icon="MessageSquareText" :title="anyFilter ? t.feedback.empty : t.feedback.emptyNone" :description="anyFilter ? 'Loosen a filter or clear them all.' : undefined">
      <Button v-if="anyFilter" variant="outline" @click="clearAll">{{ t.common.clear }}</Button>
    </EmptyState>
    <template v-else>
      <DataTable :data="items" :columns="columns" row-clickable :page-size="500" :row-key="(f) => String(f.id)" caption="Feedback" @row-click="show" />
      <div v-if="list.hasNextPage.value" class="mt-4 flex justify-center">
        <Button variant="outline" :loading="list.isFetchingNextPage.value" @click="list.fetchNextPage()">{{ t.common.loadMore }}</Button>
      </div>
    </template>

    <FeedbackSheet v-model:open="sheetOpen" :id="openId" />
  </div>
</template>
